/**
 * Єдине джерело контенту про послуги.
 *
 * Раніше ці масиви жили всередині Services.js і малювались лише на головній.
 * Тепер той самий текст потрібен і в короткому огляді на головній, і на
 * окремій сторінці кожної послуги — тож він переїхав сюди, щоб не існувати
 * у двох копіях, які поступово розійдуться.
 *
 * Порядок serviceGroups, sectionIcons і serviceMeta мусить збігатися: усі три
 * індексуються однаково. Перевірка цього стоїть нижче.
 *
 * Поля з коротким описом (title, subtitle, items, footer) показуються і на
 * головній, і на сторінці послуги. Поля розгорнутого тексту (intro, audience,
 * process, pitfalls, faq) — тільки на сторінці послуги: на головній вони
 * перетворили б огляд на полотно, а тут дають те, за чим людина приходить із
 * пошуку, і те, що Google вважає змістовною сторінкою, а не візиткою.
 */

const serviceGroups = [
  {
    title: 'Старт вашої справи «під ключ»',
    subtitle: 'Створення фундаменту для легальної та безпечної роботи.',
    intro: [
      'Реєстрація медичного бізнесу відрізняється від реєстрації будь-якого іншого тим, що майже кожне рішення на цьому етапі стає обмеженням далі. Обрана організаційно-правова форма визначає, чи зможете ви взяти партнера або продати частку. Набір КВЕДів — які саме послуги заклад має право надавати. Система оподаткування — скільки коштуватиме кожна отримана від пацієнта гривня. Помилку в цих трьох рішеннях виправляють не правкою, а перереєстрацією.',
      'Друга особливість — ліцензія на медичну практику видається не «клініці» взагалі, а конкретному суб’єкту господарювання з конкретними даними в ЄДР. Тому реєстрацію ми робимо одразу з прицілом на ліцензування: якщо структура, назва чи КВЕДи не відповідають тому, що заклад збирається робити насправді, це виявиться вже під час подання документів до МОЗ — коли приміщення орендоване, а персонал найнятий.'
    ],
    audience: [
      'Лікарям, які виходять із найму в приватну практику й обирають між ФОП і ТОВ',
      'Засновникам, що відкривають клініку вдвох або втрьох і хочуть заздалегідь зафіксувати правила виходу з бізнесу',
      'Мережам, які відкривають наступний осередок і мають вирішити: новий суб’єкт чи підрозділ наявного',
      'Чинним закладам, у яких дані в ЄДР розійшлися з фактичною діяльністю'
    ],
    items: [
      {
        label: 'Реєстрація бізнесу',
        text: 'Обираємо та реєструємо оптимальну організаційно-правову форму (ТОВ або ФОП) з урахуванням податкового планування.'
      },
      {
        label: 'Розробка структури',
        text: 'Формуємо модель управління клінікою, яка мінімізує ризики для засновників та забезпечує прозорість бізнес-процесів.'
      }
    ],
    process: [
      {
        label: 'Розмова про модель',
        text: 'На чому заклад заробляє, скільки засновників, чи планується партнер або продаж частки. Від цих відповідей залежить форма, а не навпаки.'
      },
      {
        label: 'Форма та податки',
        text: 'Порівнюємо ФОП і ТОВ на ваших цифрах: навантаження на єдиному податку й на загальній системі, наслідки для виведення прибутку та для найму лікарів.'
      },
      {
        label: 'КВЕДи під реальні послуги',
        text: 'Основний (86.21, 86.22, 86.23 — залежно від профілю) та допоміжні добираємо під перелік послуг, який заклад справді надаватиме, включно з тими, що плануються на другий рік.'
      },
      {
        label: 'Установчі документи',
        text: 'Статут для ТОВ пишемо не шаблонний: порядок ухвалення рішень, вихід учасника, доля частки. Саме за цими пунктами потім судяться.'
      },
      {
        label: 'Реєстраційні дії',
        text: 'Внесення даних до ЄДР, взяття на облік в органах, відкриття рахунку, отримання кваліфікованого електронного підпису.'
      }
    ],
    faq: [
      {
        q: 'ФОП чи ТОВ для медичної практики — що обрати?',
        a: 'Універсальної відповіді немає. ФОП простіший в адмініструванні й зазвичай дешевший у податках, але не ділиться на частки й не переживає власника. ТОВ дає партнерство, продаж частки та обмежену відповідальність, натомість вимагає бухгалтерії й формальнішого документообігу. Ліцензію на медичну практику може отримати і та, і та форма. Орієнтир простий: практика одного лікаря — зазвичай ФОП; клініка з партнерами, найманими лікарями чи планами на мережу — ТОВ.'
      },
      {
        q: 'Чи можна приймати пацієнтів одразу після реєстрації?',
        a: 'Ні. Реєстрація створює суб’єкта господарювання, а право надавати медичну допомогу дає ліцензія на медичну практику. Між цими двома подіями — приміщення, персонал, оснащення й подання документів до МОЗ.'
      },
      {
        q: 'Які КВЕДи потрібні медичному закладу?',
        a: 'Основні для медичної практики — 86.21 (загальна медична практика), 86.22 (спеціалізована медична практика) та 86.23 (стоматологічна практика); конкретний набір залежить від профілю. Часто додають КВЕДи під суміжну діяльність: лабораторні дослідження, оренду, торгівлю виробами медичного призначення. Зайвий КВЕД не шкодить, а от відсутність потрібного блокує і ліцензування, і розрахунки з контрагентами.'
      },
      {
        q: 'Скільки часу займає реєстрація?',
        a: 'Сама реєстраційна дія — питання днів. Значно більше часу забирає підготовка до неї: узгодження між засновниками, статут, вибір податкового режиму. Якщо ці рішення вже ухвалені, заклад буде зареєстрований швидко.'
      },
      {
        q: 'Чи можна пізніше перейти з ФОП на ТОВ?',
        a: 'Прямого перетворення ФОП у ТОВ законодавство не передбачає — це реєстрація нового суб’єкта господарювання. Для медичної практики це означає ще й нову ліцензію, новий запис в ЕСОЗ і переукладення договорів з пацієнтами та контрагентами. Саме тому форму варто обирати з прицілом на кілька років уперед.'
      }
    ],
    footer: 'Результат: Ви отримуєте повністю готовий до роботи суб’єкт господарювання з усіма необхідними реєстраційними документами.'
  },
  {
    title: 'Ліцензування медичної практики',
    subtitle: 'Повний супровід ліцензування медичної практики — від аудиту приміщення до моменту, коли ліцензія МОЗ у вас на руках.',
    intro: [
      'Ліцензія на медичну практику — це не документ, який отримують і кладуть у папку, а стан, у якому заклад має перебувати постійно. МОЗ видає її на підставі Відомостей про матеріально-технічну базу та персонал, і саме ці Відомості потім стають мірилом під час перевірки: усе, що в них заявлено, має існувати в реальності, а все, що змінилося, — бути вчасно оновленим.',
      'Головна причина відмов — не складність вимог, а розсинхрон між документами й дійсністю: у Відомостях зазначено кабінет, якого за площею не існує; лікар, чий сертифікат уже не чинний; обладнання без чинної метрологічної повірки. Тому роботу ми починаємо не з бланків, а з аудиту — і часто саме на цьому етапі з’ясовується, що приміщення потребує змін до подання заяви, а не після відмови.'
    ],
    audience: [
      'Тим, хто відкриває клініку або кабінет і виходить на ліцензування вперше',
      'Лікарям-ФОП, які починають приватний прийом',
      'Закладам, що додають нову спеціальність або відкривають другий підрозділ',
      'Тим, кому вже відмовили — і треба зрозуміти, що саме виправляти',
      'Чинним ліцензіатам, у яких змінилося приміщення, керівник або склад персоналу'
    ],
    items: [
      {
        label: 'Аудит відповідності',
        text: 'Перевіряємо приміщення (ДБН, ДСанПіН) та кваліфікацію персоналу на відповідність актуальним ліцензійним умовам.'
      },
      {
        label: 'Доступність та інклюзивність',
        text: 'Допомагаємо отримати обов’язковий висновок про доступність будівлі для маломобільних груп населення.'
      },
      {
        label: 'Отримання ліцензії',
        text: 'Готуємо пакет документів та супроводжуємо розгляд заяви в МОЗ до позитивного рішення.'
      }
    ],
    process: [
      {
        label: 'Аудит приміщення',
        text: 'Перевіряємо планування, площі та призначення кабінетів на відповідність ДБН і ДСанПіН для медичних закладів. Результат — перелік того, що потрібно змінити, поки це ще дешево.'
      },
      {
        label: 'Висновок про доступність',
        text: 'Організовуємо обов’язкову експертизу доступності будівлі для маломобільних груп населення.'
      },
      {
        label: 'Перевірка персоналу',
        text: 'Дипломи, спеціалізації, сертифікати й посвідчення звіряємо з ліцензійними вимогами до заявлених спеціальностей. Окремо — вимоги до керівника та медичного директора.'
      },
      {
        label: 'Оснащення й метрологія',
        text: 'Звіряємо наявне обладнання з табелем оснащення за профілем і перевіряємо чинність повірок засобів вимірювальної техніки.'
      },
      {
        label: 'Відомості та подання',
        text: 'Готуємо Відомості за чинною формою, подаємо заяву до МОЗ і супроводжуємо розгляд до наказу про видачу ліцензії.'
      },
      {
        label: 'Життя після ліцензії',
        text: 'Пояснюємо, які зміни в закладі надалі вимагають подання оновлених Відомостей і в які строки — щоб ліцензія не стала недійсною непомітно для вас.'
      }
    ],
    pitfalls: [
      'Приміщення не відповідає нормам за площею, набором чи призначенням кабінетів — а договір оренди вже підписано на кілька років',
      'Немає висновку про доступність будівлі для маломобільних груп населення',
      'У лікаря немає чинних документів саме за тією спеціальністю, яка заявляється',
      'Обладнання не відповідає табелю оснащення за профілем або не має чинної повірки',
      'Дані у Відомостях розходяться з ЄДР: адреса, назва, склад підрозділів',
      'Після отримання ліцензії заклад змінився, а оновлені Відомості до МОЗ ніхто не подав'
    ],
    faq: [
      {
        q: 'Скільки часу займає отримання ліцензії МОЗ?',
        a: 'Розгляд заяви в МОЗ має встановлений законом строк і рідко буває вузьким місцем. Реальний строк визначає підготовка. Якщо приміщення вже відповідає нормам, персонал укомплектований, а обладнання повірене — це тижні. Якщо потрібне перепланування або треба чекати на документи лікарів — місяці. Саме тому аудит робиться першим: він показує справжній строк, а не бажаний.'
      },
      {
        q: 'Чи потрібна ліцензія лікарю-ФОП?',
        a: 'Так. Ліцензія на медичну практику потрібна незалежно від організаційно-правової форми й від того, чи є у вас наймані працівники. ФОП без ліцензії не має права надавати медичну допомогу — диплома тут недостатньо.'
      },
      {
        q: 'Чому найчастіше відмовляють у ліцензії?',
        a: 'Через невідповідності, які можна було виявити заздалегідь: приміщення не відповідає нормам, немає висновку про доступність, у персоналу немає чинних документів на заявлену спеціальність, оснащення не відповідає табелю, а у Відомостях — неточності й розбіжності з реєстраційними даними.'
      },
      {
        q: 'Чи потрібна нова ліцензія при переїзді клініки?',
        a: 'Ліцензія залишається чинною, але дані про місця провадження діяльності мають бути актуальними: нове приміщення проходить ту саму перевірку на відповідність нормам, а Відомості подаються оновлені. Приймати пацієнтів за новою адресою до внесення змін не можна.'
      },
      {
        q: 'Ми додаємо нову спеціальність — що робити?',
        a: 'Кожна спеціальність заявляється окремо: під неї потрібні відповідний кабінет, оснащення за табелем і лікар з чинними документами саме за цією спеціальністю. Після цього до МОЗ подається заява про розширення провадження медичної практики разом із Відомостями — з 8 січня 2026 року для цього є окрема форма.'
      },
      {
        q: 'Що таке Відомості й коли їх оновлювати?',
        a: 'Це заявлений вами опис матеріально-технічної бази та персоналу, на підставі якого видається ліцензія. Оновлювати їх треба щоразу, коли змінюється щось із заявленого: приміщення, обладнання, керівник, склад лікарів. Окремо — Постанова КМУ № 781 зобов’язала подати оновлені Відомості за новою формою всіх, хто отримав ліцензію раніше.'
      }
    ],
    footer: 'Перевага: більшість відмов трапляється через невідповідності, які видно ще до подання заяви. Ми знаходимо їх на аудиті — коли виправити ще дешево й швидко.'
  },
  {
    title: 'Захист інтелектуальної власності',
    subtitle: 'Капіталізація вашого бренду та захист від конкурентів.',
    intro: [
      'Назва клініки — це те, у що вкладаються всі маркетингові бюджети й уся репутація, і водночас єдиний актив, який більшість закладів роками не оформлює. Поки назва не зареєстрована як торговельна марка, вона нікому не належить: конкурент може подати заявку на неї першим і після цього законно вимагати, щоб ви перестали нею користуватися — на вивісці, у рекламі, на сайті й у документах.',
      'Для медицини ризик вищий за середній. Пацієнт обирає заклад за іменем і рекомендацією, тож двійник із тією ж назвою — це не лише втрачені звернення, а й чужі відгуки та чужі помилки на вашій репутації. Зареєстрована торговельна марка дає підставу вимагати припинення, а не лише просити про нього.'
    ],
    audience: [
      'Клінікам і медичним центрам, які інвестують у впізнаваність назви',
      'Лікарям з особистим брендом — коли ім’я спеціаліста і є марка',
      'Тим, хто планує франшизу або мережу',
      'Власникам, які виявили заклад зі схожою назвою',
      'Тим, кому потрібен домен у зоні .UA'
    ],
    items: [
      {
        label: 'Реєстрація ТМ',
        text: 'Реєструємо назву медичного центру або особистого бренду лікаря. Це право на домен .UA та захист від використання вашого імені іншими.'
      },
      {
        label: 'Протидія плагіату',
        text: 'Юридично припиняємо спроби копіювання вашого бренду, дизайну чи рекламних матеріалів.'
      }
    ],
    process: [
      {
        label: 'Попередній пошук',
        text: 'Перевіряємо, чи вільне позначення: за поданими заявками й чинними свідоцтвами, з урахуванням схожих до ступеня змішування. Це набагато дешевший спосіб дізнатися про проблему, ніж відмова після року очікування.'
      },
      {
        label: 'Класи МКТП',
        text: 'Визначаємо переліки товарів і послуг, за якими реєструємо марку. Для медичного бізнесу це насамперед клас медичних послуг, часто разом з освітніми, косметологічними чи торговельними — залежно від того, чим заклад справді займається.'
      },
      {
        label: 'Заявка та експертиза',
        text: 'Готуємо й подаємо заявку, ведемо листування з експертизою, відповідаємо на запити та заперечення.'
      },
      {
        label: 'Свідоцтво й домен',
        text: 'Отримуємо свідоцтво, за потреби реєструємо домен у зоні .UA і закриваємо права на логотип у договорах із підрядниками — щоб знак, зроблений на замовлення, належав закладу, а не дизайнеру.'
      },
      {
        label: 'Захист',
        text: 'Готуємо вимоги про припинення порушення, працюємо зі скаргами на майданчиках і в соцмережах, за потреби йдемо до суду.'
      }
    ],
    faq: [
      {
        q: 'Навіщо реєструвати марку, якщо назва вже на вивісці?',
        a: 'Саме лише використання прав не створює. Свідоцтво на торговельну марку — єдине, що дозволяє заборонити іншим користуватися вашою назвою, і воно ж потрібне для реєстрації домену в зоні .UA.'
      },
      {
        q: 'Скільки триває реєстрація торговельної марки?',
        a: 'Це тривала процедура: між поданням заявки й отриманням свідоцтва проходить помітно більше року. Але пріоритет фіксується датою подання, тож захист рахується від неї, а не від дати видачі свідоцтва. Саме тому подавати варто якомога раніше, а не «коли розкрутимося».'
      },
      {
        q: 'Чи можна зареєструвати прізвище лікаря як марку?',
        a: 'Так, особисті бренди реєструють, і в медицині це поширено. Нюанси є: суто описові й загальновживані позначення реєструються складніше, тому саме формулювання марки варто продумати до подання заявки.'
      },
      {
        q: 'Конкурент використовує схожу назву — що робити?',
        a: 'Почати з перевірки, а не з листа: треба з’ясувати, хто має пріоритет, тобто хто раніше подав заявку. Далі варіанти різні — вимога про припинення, заперечення проти чужої заявки, оспорювання вже виданого свідоцтва або перемовини про співіснування.'
      },
      {
        q: 'Чи потрібна марка, якщо клініка одна й місцева?',
        a: 'Ризик менший, але не нульовий: назви найчастіше «збігаються» саме в межах одного міста, і саме там за них конкурують. До того ж зареєстрована марка — це актив, який враховують при продажу бізнесу чи запуску франшизи.'
      }
    ],
    footer: 'Результат: Ваш бренд стає захищеним активом, вартість якого зростає разом із репутацією клініки.'
  },
  {
    title: 'Реєстрація та супровід у системі ЕСОЗ',
    subtitle: 'Цифровізація вашої медичної практики згідно з вимогами законодавства.',
    note: 'Згідно з постановою Кабінету Міністрів реєстрація в ЕСОЗ є обов’язковою для всіх медичних закладів, незалежно від форми власності. Ми допомагаємо пройти цей шлях швидко та без технічних помилок.',
    intro: [
      'ЕСОЗ давно перестала бути опцією «для тих, хто працює з НСЗУ». Робота в електронній системі охорони здоров’я — це ліцензійна вимога: заклад має бути зареєстрований, керівник і лікарі — мати робочі кабінети та кваліфікований електронний підпис, а медична документація — вестися в електронному вигляді через обрану медичну інформаційну систему.',
      'Технічно процес виглядає простим, поки не почнеться. Найчастіші зупинки — не в системі, а в даних: розбіжність між тим, що зазначено в ЄДР, у ліцензійних Відомостях і в реєстраційній формі; лікар, чиї кваліфікаційні дані не підтверджуються; підрозділ, заведений не тим типом. Кожна така дрібниця повертає заклад на початок кола — і саме тому реєстрацію варто зробити один раз і правильно.'
    ],
    audience: [
      'Новим закладам одразу після отримання ліцензії',
      'Лікарям-ФОП, які починають приватний прийом',
      'Закладам, які досі не зареєстровані в системі',
      'Тим, хто змінює медичну інформаційну систему або відкриває новий підрозділ',
      'Закладам, у яких дані в ЕСОЗ розійшлися з фактичними'
    ],
    items: [
      {
        label: 'Підключення до ЕСОЗ',
        text: 'Допомагаємо обрати оптимальну МІС (Медичну інформаційну систему), яка відповідає вашому профілю роботи та бюджету.'
      },
      {
        label: 'Реєстрація суб’єкта господарювання',
        text: 'Коректне внесення даних про вашу клініку (ТОВ або ФОП), її підрозділи та обладнання в центральну базу даних.'
      },
      {
        label: 'Реєстрація керівника та персоналу',
        text: 'Налаштування кабінетів для медичного директора та медичних працівників, перевірка їхніх кваліфікаційних даних та підключення КЕП (кваліфікованого електронного підпису).'
      },
      {
        label: 'Верифікація в системі',
        text: 'Супровід процесу підтвердження даних, щоб ваш заклад отримав статус «Верифіковано».'
      }
    ],
    pitfalls: [
      'Дані про заклад в ЕСОЗ не збігаються з ЄДР — системі потрібна точна відповідність, а не «те саме іншими словами»',
      'Підпис оформлено на фізичну особу там, де потрібен підпис керівника закладу',
      'Підрозділ заведено не з тим типом або адресою, і під нього не підв’язуються місця надання послуг',
      'Лікаря зареєстровано без підтвердженої спеціальності, через що він не може підписувати документи',
      'Обрану МІС не перевірили на підтримку потрібного профілю — і це з’ясувалося вже після оплати річної підписки'
    ],
    faq: [
      {
        q: 'Чи обов’язкова реєстрація в ЕСОЗ для приватної клініки?',
        a: 'Так. Робота в електронній системі охорони здоров’я передбачена ліцензійними умовами й не залежить ані від форми власності, ані від наявності договору з НСЗУ.'
      },
      {
        q: 'Чи потрібна ЕСОЗ, якщо ми не працюємо з НСЗУ?',
        a: 'Потрібна. Договір з НСЗУ — це про фінансування, а реєстрація в ЕСОЗ — про ведення медичної документації та статус закладу. Перше без другого не існує, а друге без першого — цілком.'
      },
      {
        q: 'Що таке МІС і чи можна її змінити?',
        a: 'МІС — медична інформаційна система, через яку заклад працює з ЕСОЗ. Змінити її можна. Складність не в самій заміні, а в перенесенні даних і перепідключенні персоналу, тому виважений вибір на старті економить більше, ніж здається.'
      },
      {
        q: 'Скільки електронних підписів потрібно клініці?',
        a: 'Щонайменше — керівнику закладу й кожному медичному працівникові, який підписує документи. Підпис персональний: спільний «на клініку» не працює ні технічно, ні юридично.'
      },
      {
        q: 'Що означає статус «Верифіковано»?',
        a: 'Це підтвердження того, що подані закладом дані звірені й прийняті системою. До цього моменту реєстрацію не можна вважати завершеною, навіть якщо всі форми заповнені.'
      }
    ],
    footer: 'Результат: Ваш медичний заклад повністю відповідає вимогам Ліцензійних умов щодо обов’язкової роботи в ЕСОЗ, а персонал готовий до ведення медичної документації в електронному форматі.'
  },
  {
    title: 'Повний документальний супровід',
    subtitle: 'Правовий фундамент для безпечної та професійної діяльності клініки.',
    note: 'Готуємо документацію під конкретний профіль медичного закладу — набір документів для стоматології та для лабораторної діагностики відрізняється.',
    intro: [
      'Документи клініки згадують у двох ситуаціях: під час перевірки й під час конфлікту з пацієнтом. В обох випадках оцінюють не наявність папки, а те, чи описує вона реальні процеси закладу — і чи підписано те, що мало бути підписаним, у момент, коли це мало статися.',
      'Найдорожча помилка тут — шаблони з інтернету. Інформована згода, яка не називає конкретного втручання, не захищає лікаря. Договір, у якому не розмежовано послугу й результат, працює проти закладу. Правила внутрішнього розпорядку, що суперечать фактичному графіку, стають доказом проти вас. Тому пакет ми складаємо під профіль і під те, як заклад справді працює.'
    ],
    // Раніше весь цей перелік був частиною підзаголовка й тягнувся суцільним
    // рядком на чотири сотні символів. Окремим списком він і читається, і
    // сканується очима: людина шукає в ньому свою спеціальність.
    specialties: [
      'Загальна практика — сімейна медицина',
      'Хірургія',
      'Акушерство та гінекологія',
      'Фізична та реабілітаційна медицина',
      'Клінічна психологія',
      'Психіатрія',
      'Лабораторна діагностика',
      'Ультразвукова діагностика',
      'Стоматологія'
    ],
    audience: [
      'Закладам перед ліцензуванням — частина документів потрібна вже на цьому етапі',
      'Клінікам, які працюють на шаблонах і не переглядали їх роками',
      'Тим, хто готується до перевірки',
      'Закладам, які додають напрямок і потребують окремого пакета під нього',
      'Тим, хто вже отримав претензію або скаргу від пацієнта'
    ],
    items: [
      {
        label: 'Внутрішні нормативні документи',
        text: 'Розробляємо положення про медичний центр, правила внутрішнього розпорядку, накази та інші необхідні внутрішні нормативні документи медичного закладу.'
      },
      {
        label: 'Клієнтський пакет',
        text: 'Готуємо індивідуальні договори про надання медичних послуг, форми інформованих згод, договори публічної оферти, прейскуранти, трудові договори, посадові інструкції тощо.'
      },
      {
        label: 'Спеціалізовані документи за напрямками',
        text: 'Інфекційний контроль, утилізація відходів, захист персональних даних, комерційна таємниця, охорона праці і т.д.'
      },
      {
        label: 'Медична документація',
        text: 'СОПи, інструкції, журнали, протоколи лікування та інш.'
      }
    ],
    pitfalls: [
      'Інформована згода загального зразка, у якій не названо конкретного втручання, його альтернатив і ризиків',
      'Прейскурант, що розходиться з тим, що фактично сплачує пацієнт',
      'Договір публічної оферти на сайті, ніяк не пов’язаний із тим, що підписують у реєстратурі',
      'Немає згоди на обробку персональних даних або є така, що не покриває медичну інформацію',
      'Посадові інструкції, у яких не описано, хто саме за що відповідає — а отже, під час перевірки відповідає керівник',
      'Журнали й СОПи, які заповнюються заднім числом, і це видно з першого погляду'
    ],
    faq: [
      {
        q: 'Які документи потрібні клініці мінімально?',
        a: 'Мінімум залежить від профілю, але каркас однаковий: установчі та дозвільні документи, положення про заклад, правила внутрішнього розпорядку, договір з пацієнтом і прейскурант, форми інформованих згод, документи щодо обробки персональних даних, кадрові документи з посадовими інструкціями, а також внутрішні документи з інфекційного контролю, поводження з медичними відходами й охорони праці. Далі додається специфіка спеціальності.'
      },
      {
        q: 'Чи можна взяти типові шаблони з інтернету?',
        a: 'Взяти можна, покластися — ні. Шаблон описує абстрактний заклад, а перевірку й суд цікавить ваш: ваш перелік послуг, ваші приміщення, ваш персонал. Документ, який суперечить фактичним процесам, гірший за його відсутність, бо перетворюється на доказ проти закладу.'
      },
      {
        q: 'Чи захищає інформована згода лікаря?',
        a: 'Захищає — якщо в ній названо конкретне втручання, його альтернативи, ризики й очікуваний результат, і якщо вона підписана до втручання, а не разом з рештою паперів на виході. Загальна згода «на медичну допомогу» у спорі важить мало.'
      },
      {
        q: 'Як часто оновлювати документи?',
        a: 'Щоразу, коли змінюється законодавство або сам заклад: новий напрямок, нове обладнання, нові позиції в прейскуранті, зміна керівника. Планово пакет варто переглядати щонайменше раз на рік.'
      },
      {
        q: 'Чи потрібні окремі документи щодо персональних даних?',
        a: 'Так. Медичні дані належать до особливо чутливих, і робота з ними вимагає і згоди пацієнта, і внутрішніх правил: хто має доступ, як дані зберігаються, що відбувається з доступом при звільненні працівника.'
      }
    ],
    footer: 'Мета: Забезпечити повну юридичну відповідність внутрішніх процесів законодавству України, що мінімізує ризики для правових спорів та гарантує стабільність роботи закладу.'
  },
  {
    title: 'Юридичний аутсорсинг',
    subtitle: 'Правова підтримка Вашого бізнесу.',
    intro: [
      'Клініці рідко потрібен юрист «на постійно» — але потрібен той, хто відповість сьогодні. Договір із підрядником, який прийшов на підпис уранці. Перевірка, що стоїть на порозі. Пацієнт, який написав скаргу. Лікар, що звільняється й забирає із собою базу. У кожної з цих ситуацій короткий строк реакції, і саме в ньому вузьке місце, а не у складності права.',
      'Абонентське обслуговування закриває саме це: у закладу є команда, яка вже знає його документи, структуру й історію, тож не витрачає тиждень на входження в контекст. Для медичного бізнесу різниця відчутна — тут майже кожне питання одночасно і господарське, і медичне, і про персональні дані.'
    ],
    audience: [
      'Клінікам без штатного юриста, у яких питання виникають щотижня',
      'Закладам зі штатним юристом, якому потрібне галузеве підсилення',
      'Мережам, де документообіг уже не вміщується в ручний режим',
      'Закладам, що готуються до перевірки або вже її проходять',
      'Тим, хто виходить на гранти, тендери чи міжнародні контракти'
    ],
    items: [
      {
        label: 'Супровід діяльності',
        text: 'Повний або частковий юридичний супровід господарської та медичної діяльності бізнесу.'
      },
      {
        label: 'Тематика супроводу',
        text: 'Від щоденної договірної роботи до представництва в судах і перевірках. Повний перелік напрямків — нижче.'
      },
      {
        label: 'Перевага',
        text: 'Ви отримуєте підтримку команди, для якої медичне право — основна спеціалізація, а не одна з практик між іншими.'
      },
      {
        label: 'Особливості',
        text: 'Незважаючи на спеціалізацію в медичному праві, партнерами компанії VMG є експерти в різних галузях права, зокрема, сімейному, цивільному, земельному, кримінальному, антимонопольному. Тому ми надамо Вам можливість не перейматися юридичними питаннями чи проблемами, які виникатимуть у медичному чи іншому бізнесі, а також у Вашому особистому житті.'
      }
    ],
    process: [
      {
        label: 'Вхідний аудит',
        text: 'Дивимося наявні документи, договори й процеси, фіксуємо, де найбільші ризики і з чого починати.'
      },
      {
        label: 'Погоджений обсяг',
        text: 'Домовляємося, що входить у щомісячну роботу, а що вважається окремим проєктом. Без цього абонплата рано чи пізно перетворюється на суперечку про межі.'
      },
      {
        label: 'Один канал звернення',
        text: 'Питання не губляться в особистих переписках: у закладу є місце, куди вони надходять, і строк, у який на них відповідають.'
      },
      {
        label: 'Робота на випередження',
        text: 'Повідомляємо про зміни в законодавстві, що стосуються саме вашого профілю, до того, як вони стануть вашою проблемою.'
      },
      {
        label: 'Представництво',
        text: 'Якщо доходить до перевірки, претензії чи суду — вступаємо у справу, вже знаючи матеріали, а не знайомлячись із ними в останню ніч.'
      }
    ],
    faq: [
      {
        q: 'Чим це відрізняється від разових консультацій?',
        a: 'Швидкістю й контекстом. Разова консультація щоразу починається зі знайомства із ситуацією — це час і гроші. На абонементі юрист уже знає ваші договори, структуру й попередні спори, тому відповідає одразу, а частину проблем знімає ще до того, як ви їх помітили.'
      },
      {
        q: 'Що робити, коли до клініки прийшла перевірка?',
        a: 'Насамперед — не залишати перевіряльників наодинці з персоналом і зафіксувати підстави перевірки. Далі вирішальним стає те, що саме потрапить до акта: заперечення подаються у визначені строки, і мовчання на цьому етапі коштує дорого. На абонентському обслуговуванні ми підключаємося з моменту, коли перевірка тільки прийшла.'
      },
      {
        q: 'Чи працюєте ви з немедичними питаннями?',
        a: 'Так. Медичне право — наша спеціалізація, але заклад живе не лише в ньому: оренда, поставка, підряд, трудові спори, стягнення заборгованості. Партнери компанії закривають суміжні галузі — від корпоративного й земельного до кримінального права.'
      },
      {
        q: 'Чи можна замовити лише один напрямок?',
        a: 'Можна. Частина клієнтів починає з аудиту документів або із супроводу однієї угоди й переходить на постійне обслуговування вже після цього.'
      },
      {
        q: 'Чи допомагаєте з ліцензіями Держлікслужби?',
        a: 'Так — зокрема з ліцензіями на діяльність з обігу наркотичних засобів, психотропних речовин і прекурсорів, які потрібні закладам відповідного профілю.'
      }
    ],
    /**
     * Той самий перелік, що раніше стояв одним реченням на дев'яносто слів
     * усередині пункту «Тематика супроводу». Словами його не дочитували до
     * середини; списком людина знаходить у ньому свій пункт поглядом.
     */
    coverage: {
      title: 'Що входить у супровід',
      items: [
        'Договірна робота: оренда, поставка, підряд, надання послуг, зовнішньоекономічні договори',
        'Тендерна документація та участь у закупівлях',
        'Претензійно-позовна робота',
        'Представництво в судових, правоохоронних і контролюючих органах, супровід перевірок',
        'Ліцензії Держлікслужби на діяльність з обігу наркотичних засобів і психотропних речовин',
        'Внесення закладу до переліку реабілітаційних установ для дітей з інвалідністю за бюджетні кошти',
        'Консультації з питань медичної та господарської діяльності закладу',
        'Супровід міжнародних і державних грантів',
        'Аудит наявної документації з письмовими висновками'
      ]
    },
    footer: null
  }
];

const launchStages = [
  {
    number: '01',
    title: 'Юридичне проєктування',
    items: [
      { label: 'Вибір організаційно-правової форми', text: 'ТОВ (для мережі/партнерства) або ФОП (для приватної практики).' },
      { label: 'Підбір КВЕДів', text: 'Основний (86.21, 86.22 тощо) та допоміжні.' },
      { label: 'Система оподаткування', text: 'Розрахунок податкового навантаження (єдиний податок чи загальна система).' },
      { label: 'Реєстрація', text: 'Підготовка статуту (для ТОВ), внесення даних в ЄДР.' }
    ]
  },
  {
    number: '02',
    title: 'Приміщення та інклюзивність',
    items: [
      { label: 'Аудит локації', text: 'Перевірка відповідності ДБН (державним будівельним нормам) для медичних закладів та кабінетів.' },
      { label: 'Отримання висновку про інклюзивність', text: 'Обов’язкова експертиза щодо доступності приміщення для маломобільних груп населення.' },
      { label: 'Договірна база', text: 'Укладання договору оренди з урахуванням специфіки медичної діяльності.' }
    ]
  },
  {
    number: '03',
    title: 'Кадрове забезпечення',
    items: [
      { label: 'Перевірка кваліфікації', text: 'Аналіз дипломів, сертифікатів та посвідчень лікарів на відповідність ліцензійним вимогам.' },
      { label: 'Штатний розпис', text: 'Формування структури персоналу (медичний директор, медичний персонал, адміністрація).' },
      { label: 'Трудові договори', text: 'Оформлення відносин з персоналом.' }
    ]
  },
  {
    number: '04',
    title: 'Матеріально-технічна база',
    items: [
      { label: 'Табель оснащення', text: 'Перевірка наявності необхідного обладнання згідно з профілем діяльності.' },
      { label: 'Метрологія', text: 'Перевірка засобів вимірювальної техніки.' }
    ]
  },
  {
    number: '05',
    title: 'Отримання ліцензії МОЗ',
    items: [
      { label: 'Підготовка Відомостей', text: 'Детальний опис бази, персоналу та обладнання.' },
      { label: 'Подача документів', text: 'Взаємодія з Міністерством охорони здоров’я.' },
      { label: 'Контроль результату', text: 'Моніторинг наказів МОЗ та отримання ліцензії.' }
    ]
  },
  {
    number: '06',
    title: 'Старт роботи та безпека',
    items: [
      { label: 'Підключення до ЕСОЗ', text: 'Вибір та впровадження медичної інформаційної системи (МІС).' },
      { label: 'Документальна база', text: 'Розробка внутрішньої та медичної документації (положення про медичний центр, договору публічної оферти, згод на медичне втручання, прейскуранту, правил внутрішнього розпорядку, посадових інструкцій та інш.).' },
      { label: 'Захист бренду', text: 'Подання заявки на реєстрацію Торговельної Марки (ТМ).' }
    ]
  }
];

const sectionIcons = [
  'fas fa-sitemap',         // 1. Старт
  'fas fa-id-card',         // 2. Ліцензування
  'fas fa-shield-halved',   // 3. Захист ІВ
  'fas fa-database',        // 4. ЕСОЗ
  'fas fa-file-contract',   // 5. Документація
  'fas fa-handshake'        // 6. Аутсорсинг
];

/**
 * Адреса, заголовки й опис для сторінки кожної послуги.
 *
 * Назва в serviceGroups — для меню, футера, крихт і карток: «Старт вашої
 * справи «під ключ»» там на місці. Але людина шукає не її, а «реєстрацію ФОП
 * для медичної практики», тож для пошуку тут окремі поля:
 *   h1 — заголовок самої сторінки послуги, тими словами, якими її шукають;
 *   title — <title> без бренду (його дописує Seo), щоб разом із суфіксом
 *     вкластися в 60 знаків;
 *   description — 140–160 знаків, головна фраза на початку: коротший опис
 *     Google добирає сам зі сторінки, довший обрізає.
 *
 * banner — фото напрямку. Те саме стоїть на картці послуги на головній і
 * банером на сторінці послуги: людина, яка клікнула картку, впізнає кадр.
 * Фото з Pexels (ліцензія дозволяє комерційне використання без вказання
 * автора); джерела — docs/photo-sources.md. До цього тут стояли постери
 * hero-відео, і половина з них не мала стосунку до теми: йога в горах на
 * сторінці про торговельні марки.
 *
 * cta — заклик у кінці сторінки. Раніше на всіх шести сторінках стояв один
 * і той самий текст. Людина, яка щойно прочитала про причини відмов у
 * ліцензії, готова до конкретнішої пропозиції — і саме її тут і отримує.
 *
 * Формулювання — проста обіцянка дії («перевіримо», «підберемо»), а не заклик
 * до спільної дії («перевірмо», «подивімось»): друге для юридичної компанії
 * звучить надто фамільярно.
 */
export const serviceMeta = [
  { slug: 'reyestraciya-medychnogo-biznesu',
    h1: 'Реєстрація ФОП і ТОВ для медичної практики',
    title: 'Реєстрація ФОП і ТОВ для клініки',
    description: 'Реєстрація ФОП або ТОВ для медичної практики: вибір форми й податкової системи, КВЕДи 86.21–86.23, статут, внесення до ЄДР — з прицілом на ліцензію МОЗ.',
    banner: '/images/service-registration-6c8bebee.jpg',
    cta: { title: 'Підберемо форму, КВЕДи й податковий режим', text: 'Скажіть, скільки засновників, який профіль і чи будуть наймані лікарі, — і ми назвемо форму, КВЕДи та податковий режим, які підійдуть саме вам.' } },
  { slug: 'licenzuvannya-medychnoyi-praktyky',
    h1: 'Отримання ліцензії на медичну практику',
    title: 'Ліцензія на медичну практику від МОЗ',
    description: 'Ліцензія на медичну практику від МОЗ: аудит приміщення за ДБН і ДСанПіН, перевірка персоналу й оснащення, Відомості за чинною формою, супровід до наказу.',
    banner: '/images/service-licensing-d044e4d1.jpg',
    cta: { title: 'Перевіримо готовність до подання', text: 'Більшість відмов видно ще до подання заяви. Розкажіть про приміщення, персонал і обладнання — скажемо, що потрібно виправити і скільки це займе.' } },
  { slug: 'zahyst-intelektualnoyi-vlasnosti',
    h1: 'Реєстрація й захист торговельної марки клініки',
    title: 'Реєстрація торговельної марки клініки',
    description: 'Реєстрація торговельної марки клініки чи особистого бренду лікаря: перевірка назви, класи МКТП, заявка й експертиза, домен .UA і захист від копіювання бренду.',
    banner: '/images/service-trademark-3a2fbf55.jpg',
    cta: { title: 'Перевіримо, чи вільна ваша назва', text: 'Попередній пошук показує, чи можна зареєструвати назву й чи не порушує вона чужих прав, — до того, як ви вкладетеся у вивіску, сайт і рекламу.' } },
  { slug: 'reyestraciya-v-esoz',
    h1: 'Реєстрація в ЕСОЗ для клініки та лікаря-ФОП',
    title: 'Реєстрація клініки в ЕСОЗ (eHealth)',
    description: 'Реєстрація клініки й лікаря-ФОП в ЕСОЗ (eHealth): вибір МІС, внесення закладу й підрозділів, кабінети лікарів, КЕП і верифікація — без повернень на початок.',
    banner: '/images/service-esoz-698fd986.jpg',
    cta: { title: 'Підключимо заклад до ЕСОЗ', text: 'Скажіть, чи є вже КЕП, обрана МІС і зареєстрований суб’єкт господарювання, — і ми назвемо, що саме лишилося зробити.' } },
  { slug: 'dokumentalnyy-suprovid-kliniky',
    h1: 'Розробка документів для медичного закладу',
    title: 'Документи для медичного закладу',
    description: 'Документи для медичного закладу під ваш профіль: положення, договори з пацієнтами, інформовані згоди, обробка персональних даних, СОПи, журнали й інструкції.',
    banner: '/images/service-documents-f1b5a713.jpg',
    cta: { title: 'Перевіримо наявні документи', text: 'Надішліть перелік наявних документів — скажемо, чого бракує під ваш профіль і що з наявного не витримає перевірки.' } },
  { slug: 'yurydychnyy-autsorsyng',
    h1: 'Юридичний супровід клініки на абонементі',
    title: 'Юридичний супровід медичного закладу',
    description: 'Юридичний супровід медичного закладу на абонементі: договори, перевірки, претензії пацієнтів, суди. Команда, для якої медичне право — основна спеціалізація.',
    banner: '/images/service-outsourcing-85195fd8.jpg',
    cta: { title: 'Розрахуємо обсяг супроводу', text: 'Розкажіть, скільки в закладі договорів і питань на місяць, чи є штатний юрист і що зараз болить найбільше, — і ми запропонуємо формат.' } },
];

if (serviceMeta.length !== serviceGroups.length || sectionIcons.length !== serviceGroups.length) {
  throw new Error('serviceGroups, sectionIcons і serviceMeta мають бути однакової довжини');
}

export const findService = (slug) => {
  const index = serviceMeta.findIndex((m) => m.slug === slug);
  if (index === -1) return null;
  return { ...serviceGroups[index], meta: serviceMeta[index], icon: sectionIcons[index], index };
};

export { serviceGroups, launchStages, sectionIcons };
